import { useCallback, type RefObject } from 'react';
import {
  buildDragState,
  capturePointer,
  getMatchingPointerState,
  isButtonTarget,
  isPrimaryPointerStart,
  pinPopoverRect,
  readViewportSize,
  releasePointerIfCaptured,
  resolveDraggedRect,
} from './CmarkdowntablePopoverPointerHandlers.helpers';
import type {
  CmarkdowntablePopoverRect,
  PopoverDragState,
  TablePopoverPointerHandler,
} from './Cmarkdowntable.types';

interface UseCmarkdowntablePopoverDragHandlersInput {
  dragStateRef: RefObject<PopoverDragState | null>;
  popoverRef: RefObject<HTMLDivElement | null>;
  applyManualRect: (rect: CmarkdowntablePopoverRect | null) => void;
}

interface CmarkdowntablePopoverDragHandlers {
  handleHeaderPointerDown: TablePopoverPointerHandler;
  handleHeaderPointerMove: TablePopoverPointerHandler;
  handleHeaderPointerEnd: TablePopoverPointerHandler;
}

export function useCmarkdowntablePopoverDragHandlers(
  input: UseCmarkdowntablePopoverDragHandlersInput,
): CmarkdowntablePopoverDragHandlers {
  const { dragStateRef, popoverRef, applyManualRect } = input;

  const handleHeaderPointerDown = useCallback<TablePopoverPointerHandler>((event): void => {
    if (!isPrimaryPointerStart(event) || isButtonTarget(event.target)) {
      return;
    }

    const pinned = pinPopoverRect({ popoverRef, applyManualRect });
    if (!pinned) {
      return;
    }

    dragStateRef.current = buildDragState({ event, pinned });
    capturePointer(event);
    event.preventDefault();
  }, [applyManualRect, dragStateRef, popoverRef]);

  const handleHeaderPointerMove = useCallback<TablePopoverPointerHandler>((event): void => {
    const drag = getMatchingPointerState({ stateRef: dragStateRef, pointerId: event.pointerId });
    if (!drag) {
      return;
    }

    applyManualRect(resolveDraggedRect({
      drag,
      pointerX: event.clientX,
      pointerY: event.clientY,
      viewport: readViewportSize(),
    }));
  }, [applyManualRect, dragStateRef]);

  const handleHeaderPointerEnd = useCallback<TablePopoverPointerHandler>((event): void => {
    const drag = getMatchingPointerState({ stateRef: dragStateRef, pointerId: event.pointerId });
    if (!drag) {
      return;
    }

    dragStateRef.current = null;
    releasePointerIfCaptured(event);
  }, [dragStateRef]);

  return {
    handleHeaderPointerDown,
    handleHeaderPointerMove,
    handleHeaderPointerEnd,
  };
}
